import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ArrowRight, Sparkle } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';

/**
 * UpsellStrip — calm, non-blocking strip that invites users to upgrade
 * WITHOUT hiding the page. Meant for reference / discovery pages where
 * engagement matters more than a paywall.
 *
 * Usage:
 *   <UpsellStrip
 *     feature="soil_analysis"
 *     headline="Save every soil test, forever."
 *     body="Homeowner Seasonal unlocks unlimited test history, zone-by-zone tracking, and email reminders."
 *     ctaLabel="Start Free Trial"
 *   />
 *
 * Automatically hides when user already has the feature.
 */
const TIER_INFO = {
  homeowner_seasonal: { name: 'Homeowner Seasonal', price: '$50', period: '6 mo', trialCopy: '1 month FREE trial' },
  enthusiast_annual: { name: 'Enthusiast Annual', price: '$80', period: 'year', trialCopy: '2 months FREE trial' },
};

const FEATURE_REQUIRED_TIER = {
  weed_department: 'homeowner_seasonal',
  equipment_log: 'homeowner_seasonal',
  spreader_settings: 'homeowner_seasonal',
  email_alerts: 'homeowner_seasonal',
  zone_management: 'homeowner_seasonal',
  field_guide: 'homeowner_seasonal',
  pest_department: 'enthusiast_annual',
  disease_department: 'enthusiast_annual',
  soil_analysis: 'enthusiast_annual',
  spray_forecast: 'enthusiast_annual',
  unlimited_zones: 'enthusiast_annual',
  priority_support: 'enthusiast_annual',
};

export const UpsellStrip = ({
  feature,
  headline,
  body,
  ctaLabel = 'Start Free Trial',
  testId,
}) => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);

  // Hide entirely if user already has access, or not logged in (landing copy handles it)
  if (!user || user?.allowed_features?.includes(feature)) return null;

  const requiredTier = FEATURE_REQUIRED_TIER[feature] || 'homeowner_seasonal';
  const tierInfo = TIER_INFO[requiredTier];

  const handleCheckout = async (packageId) => {
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { package_id: packageId },
        { withCredentials: true, headers: { origin } }
      );
      window.location.href = data.url;
    } catch {
      toast.error('Failed to create checkout session');
    }
  };

  return (
    <>
      <div
        className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background p-5 sm:p-6 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between"
        data-testid={testId || `upsell-strip-${feature}`}
      >
        <div className="flex gap-3 items-start">
          <div className="flex-shrink-0 mt-0.5">
            <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center">
              <Sparkle weight="fill" size={18} className="text-primary" />
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wider uppercase text-primary mb-1">
              Unlock more with {tierInfo.name}
            </div>
            <div className="text-lg font-bold text-foreground leading-tight mb-1" data-testid={`${testId || 'upsell'}-headline`}>
              {headline}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {body}
            </p>
          </div>
        </div>
        <Button
          onClick={() => setOpenDialog(true)}
          className="bg-primary hover:bg-primary/90 shrink-0 self-start sm:self-center"
          data-testid={`${testId || 'upsell'}-cta`}
        >
          {ctaLabel}
          <ArrowRight weight="bold" size={14} className="ml-1.5" />
        </Button>
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="sm:max-w-2xl" data-testid={`${testId || 'upsell'}-dialog`}>
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className={`border-2 border-green-500 relative overflow-hidden ${requiredTier === 'homeowner_seasonal' ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}>
              <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">POPULAR</div>
              <CardHeader>
                <CardTitle className="text-green-700 dark:text-green-400">Homeowner Seasonal</CardTitle>
                <p className="text-sm text-muted-foreground">Perfect for lawn season (6 months)</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">$50<span className="text-lg font-normal text-muted-foreground">/6 mo</span></div>
                  <div className="text-sm text-green-600 font-medium">1 month FREE trial included</div>
                </div>
                <Button onClick={() => handleCheckout('homeowner_seasonal')} className="w-full bg-green-600 hover:bg-green-700" data-testid={`${testId || 'upsell'}-dialog-seasonal-btn`}>
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
            <Card className={`border-2 border-blue-500 relative overflow-hidden ${requiredTier === 'enthusiast_annual' ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}>
              <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">BEST VALUE</div>
              <CardHeader>
                <CardTitle className="text-blue-700 dark:text-blue-400">Enthusiast Annual</CardTitle>
                <p className="text-sm text-muted-foreground">Full year, ALL features unlocked</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-3xl font-bold">$80<span className="text-lg font-normal text-muted-foreground">/year</span></div>
                  <div className="text-sm text-blue-600 font-medium">2 months FREE trial included</div>
                </div>
                <Button onClick={() => handleCheckout('enthusiast_annual')} className="w-full bg-blue-600 hover:bg-blue-700" data-testid={`${testId || 'upsell'}-dialog-annual-btn`}>
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
          </div>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Cancel anytime during your free trial. No charge until trial ends.
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpsellStrip;
